import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MaBottomNav, MaCircleButton, MaIcon } from "../components/ui";
import { Screen } from "../components/layout/Screen";
import {
  CATEGORY_LABEL, CHECKLIST, TOTAL, type ChecklistCategory, type ChecklistItem,
} from "../data/beenThere";
import { getChecked, resetChecked, toggleChecked } from "../lib/beenThereStore";

const CATEGORY_ORDER: ChecklistCategory[] = ["icons", "food", "neighborhoods", "hidden", "seasonal"];

export function BeenThere() {
  const navigate = useNavigate();
  // Mirror the localStorage set in React state so toggles re-render
  // without prop drilling. setChecked also writes through.
  const [checked, setChecked] = useState<Set<string>>(() => getChecked());

  const toggle = (id: string) => {
    toggleChecked(id);
    setChecked(getChecked());
  };

  const reset = () => {
    if (!confirm("Reset every checkbox? This can't be undone.")) return;
    resetChecked();
    setChecked(new Set());
  };

  const grouped = useMemo(() => {
    const out: Record<ChecklistCategory, ChecklistItem[]> = {
      icons: [], food: [], neighborhoods: [], hidden: [], seasonal: [],
    };
    for (const item of CHECKLIST) out[item.category].push(item);
    return out;
  }, []);

  const doneCount = checked.size;
  const pct = Math.round((doneCount / TOTAL) * 100);

  return (
    <Screen footer={<MaBottomNav active="saved" />}>
      {/* Header */}
      <div style={{
        padding: "calc(env(safe-area-inset-top, 20px) + 28px) 18px 12px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      }}>
        <button onClick={() => navigate(-1)} style={{
          background: "transparent", border: "none", padding: 0,
          color: "var(--ma-ink)", fontFamily: "var(--ma-mono)", fontSize: 11,
          letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer",
        }}>← Back</button>
        <MaCircleButton icon="bookmark" label="Reset" onClick={reset} />
      </div>

      <div style={{ padding: "8px 18px 14px" }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--ma-brick)", marginBottom: 6,
        }}>
          your hit list
        </div>
        <div className="ma-display" style={{ fontSize: 36, lineHeight: 0.95, color: "var(--ma-ink)", marginBottom: 12 }}>
          Been there<span style={{ color: "var(--ma-brick)" }}>,</span><br />done that.
        </div>
        <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)", lineHeight: 1.4, marginBottom: 14 }}>
          A loose canon of 26 things to actually experience in Amsterdam. Check 'em off when you've done them.
        </div>

        {/* Progress bar */}
        <div style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between",
          marginBottom: 6,
        }}>
          <div className="ma-mono" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ma-ink)", fontWeight: 600 }}>
            {doneCount} of {TOTAL} done
          </div>
          <div className="ma-mono" style={{ fontSize: 11, letterSpacing: "0.04em", color: "var(--ma-ink-soft)" }}>
            {pct}%
          </div>
        </div>
        <div style={{
          height: 10,
          background: "var(--ma-cream)",
          border: "var(--ma-border) solid var(--ma-ink)",
          borderRadius: 6, overflow: "hidden",
        }}>
          <div style={{
            width: `${pct}%`, height: "100%",
            background: pct === 100 ? "var(--ma-canal)" : "var(--ma-brick)",
            transition: "width 240ms ease",
          }} />
        </div>
        {pct === 100 && (
          <div className="ma-mono" style={{
            fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
            color: "var(--ma-canal)", marginTop: 8, fontWeight: 600,
          }}>
            ✓ certified Amsterdammer · the list is yours
          </div>
        )}
      </div>

      {/* Sections */}
      <div style={{ padding: "0 18px 18px" }}>
        {CATEGORY_ORDER.map((cat) => (
          <Section
            key={cat}
            title={CATEGORY_LABEL[cat]}
            items={grouped[cat]}
            checked={checked}
            onToggle={toggle}
          />
        ))}
      </div>

      {/* Attribution footer — credit the source piece. */}
      <div style={{ padding: "18px 18px 30px" }}>
        <div style={{
          padding: "14px 16px",
          background: "var(--ma-cream)",
          border: "1px dashed var(--ma-line)",
          borderRadius: "var(--ma-card-radius)",
        }}>
          <div className="ma-mono" style={{
            fontSize: 9, letterSpacing: "0.18em",
            textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 6,
          }}>
            as seen on
          </div>
          <div style={{ fontFamily: "var(--ma-body)", fontSize: 13, color: "var(--ma-ink)", lineHeight: 1.4 }}>
            Most of these picks are inspired by Amsterdamian's first-visit list — a great long read if you want the full prose.
          </div>
          <a
            href="https://amsterdamian.com/see/photos/things-not-to-miss-in-amsterdam-first-visit/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8,
              fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.1em",
              color: "var(--ma-brick)", textDecoration: "underline",
            }}
          >
            amsterdamian.com ↗
          </a>
        </div>
      </div>
    </Screen>
  );
}

function Section({
  title, items, checked, onToggle,
}: {
  title: string;
  items: ChecklistItem[];
  checked: Set<string>;
  onToggle: (id: string) => void;
}) {
  const doneInSection = items.filter((i) => checked.has(i.id)).length;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="ma-mono" style={{
          fontSize: 11, letterSpacing: "0.14em",
          textTransform: "uppercase", color: "var(--ma-ink)", fontWeight: 600,
        }}>
          {title}
        </div>
        <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-ink-soft)" }}>
          {doneInSection} / {items.length}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it) => (
          <ItemRow key={it.id} item={it} done={checked.has(it.id)} onToggle={() => onToggle(it.id)} />
        ))}
      </div>
    </div>
  );
}

function ItemRow({ item, done, onToggle }: { item: ChecklistItem; done: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12,
        padding: "12px 14px", width: "100%", textAlign: "left",
        background: done ? "var(--ma-cream)" : "var(--ma-paper)",
        border: "var(--ma-border) solid var(--ma-ink)",
        borderRadius: "var(--ma-card-radius)",
        cursor: "pointer", color: "inherit",
        opacity: done ? 0.78 : 1,
        transition: "opacity 160ms ease, background 160ms ease",
      }}
    >
      {/* Checkbox */}
      <div style={{
        width: 26, height: 26, flexShrink: 0,
        borderRadius: 6,
        border: "var(--ma-border) solid var(--ma-ink)",
        background: done ? "var(--ma-brick)" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--ma-paper)",
        transition: "background 160ms ease",
      }}>
        {done && <MaIcon name="check" size={16} stroke={2.6} color="var(--ma-paper)" />}
      </div>

      <div style={{ fontSize: 20, lineHeight: 1, flexShrink: 0, marginTop: 1 }} aria-hidden>
        {item.emoji}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 16,
          color: "var(--ma-ink)", lineHeight: 1.15,
          textDecoration: done ? "line-through" : "none",
          textDecorationColor: done ? "var(--ma-ink-soft)" : undefined,
        }}>
          {item.title}
        </div>
        <div style={{
          fontFamily: "var(--ma-body)", fontSize: 13,
          color: "var(--ma-ink-soft)", lineHeight: 1.4, marginTop: 3,
        }}>
          {item.description}
        </div>
      </div>
    </button>
  );
}
